<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'

import { useI18n } from '@/app/i18n'
import AppIcon from '@/shared/ui/AppIcon.vue'

import CommandPalette from './CommandPalette.vue'

/**
 * The navbar's search pill: a button drawn like a field that opens the palette (`CommandPalette.vue`), with the
 * shortcut shown; ⌘K / Ctrl+K opens it from anywhere. `compact`: the icon alone (below 992 px).
 */
defineProps<{ compact?: boolean }>()
const { t } = useI18n()
const open = ref(false)
const mac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent)
const shortcut = mac ? '⌘K' : 'Ctrl K'

function onKeydown(event: KeyboardEvent): void {
  // `code` is the physical key, so the shortcut works on a Cyrillic or Greek layout too; `key` is the fallback.
  const isK = event.code === 'KeyK' || event.key.toLowerCase() === 'k'
  if ((event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && isK) {
    event.preventDefault()
    open.value = !open.value
  }
}
onMounted(() => window.addEventListener('keydown', onKeydown))
onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown))
</script>

<template>
  <!-- One root, so a class or style from the navbar lands here (a fragment would drop it). -->
  <div class="navbar-search-host">
    <button
      type="button"
      class="navbar-search"
      :class="{ 'is-compact': compact }"
      :aria-label="t('shell.navbar.action.search')"
      :aria-keyshortcuts="mac ? 'Meta+K' : 'Control+K'"
      data-testid="navbar-search"
      @click="open = true"
    >
      <AppIcon name="SearchIcon" :size="16" class="navbar-search__icon" />
      <span v-if="!compact" class="navbar-search__text">{{ t('shell.navbar.action.search') }}</span>
      <kbd v-if="!compact" class="navbar-search__kbd" aria-hidden="true">{{ shortcut }}</kbd>
    </button>
    <CommandPalette v-model:visible="open" />
  </div>
</template>

<style scoped>
.navbar-search-host {
  display: flex;
  min-width: 0;
}
.navbar-search {
  display: flex;
  gap: 0.7rem;
  align-items: center;
  width: 400px;
  max-width: 100%;
  height: 40px;
  padding: 0 0.5rem 0 1rem;
  font: inherit;
  font-size: 0.929rem;
  color: var(--p-surface-600);
  text-align: left;
  cursor: text;
  background: var(--app-fill);
  border: 0;
  border-radius: 20px;
  transition: background 0.2s ease;
}
.navbar-search:hover {
  background: var(--app-fill-hover);
}
.navbar-search:focus-visible {
  outline: 2px solid var(--p-primary-300);
  outline-offset: 2px;
}
.navbar-search.is-compact {
  justify-content: center;
  width: 40px;
  padding: 0;
  cursor: pointer;
}
.navbar-search__icon {
  flex: 0 0 auto;
}
.navbar-search__text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.navbar-search__kbd {
  flex: 0 0 auto;
  padding: 2px 6px;
  font-family: inherit;
  font-size: 0.786rem;
  font-weight: 600;
  color: var(--p-surface-500);
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-md);
}
</style>
